import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  ModalComponent,
  nameof,
  SapphireBlueButton,
  SingleSelect,
  ToastService,
} from "@mindmemobile/mm-shared-global"; 
import PurchaseLinkVariantTable from "./purchase-link/components/PurchaseLinkVariantTable";
import PurchaseLinkAdvancedSection from "./purchase-link/components/PurchaseLinkAdvancedSection";
import PurchaseLinkRelatedItems from "./purchase-link/components/PurchaseLinkRelatedItems";
import { 
  ItemDetailPageModel,
  RelatedItemsModel,
  RelatedSectionModel, 
  MerchantAccount, 
  SellerModel, 
  PurchaseLinkModel, 
} from "../shared/models";
import { PurchaseLinkStore } from "../shared/store";
import { AuthService, MerchantAccountService, PurchaseLinkService, SellerService } from "../shared/services";
import { ApplicationStatus, PurchaseLinkPriceOption, PurchaseVariantSelectionType, SystemItemTypes } from "../shared/enums";
import { getVariantOptions, HalfContainer, priceOptions, PurchaseLinkPopupWrapper } from "../shared/styles";
import { CarouselComponent, NavPills } from "../shared/components";


type IProps = {
  purchaseLinkStore: PurchaseLinkStore;
  onClose: (refresh: boolean) => void;
  isEdit?: boolean;
};  

const promotionOptions = [{ label: 'None', value: 'None' } as IOption, { label: 'All', value: 'All' } as IOption];

const PurchaseLinkPopUp: React.FC<IProps> = ({ purchaseLinkStore, onClose, isEdit }) => {
  const { purchaseLinkModel } = purchaseLinkStore;
  const [activeNavPill, setActiveNavPill] = useState('details');

  useEffect(() => {
      getMerchantAccounts();
      loadSellers();
  }, []);

  const getMerchantAccounts = () => {
      MerchantAccountService.getMerchantAccounts(AuthService.authData.accountId, 1, 10000, '').subscribe(
          (response) => {
              if (response.result) {
                  const activeMerchantAccounts = (
                      response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) || []
                  ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

                  purchaseLinkStore.updateMerchantAccounts(activeMerchantAccounts);
                  purchaseLinkStore.updatePurchaseLink(
                      new PurchaseLinkModel({
                          ...purchaseLinkStore.purchaseLinkModel,
                          selectedMerchantAccount:
                              purchaseLinkModel.selectedMerchantAccount.name === ''
                                  ? activeMerchantAccounts[0]
                                  : purchaseLinkStore.purchaseLinkModel.selectedMerchantAccount,
                      }),
                  );
              }
          },
      );
  };

  function loadSellers() {
      SellerService.getSellerNames(AuthService.authData.accountId).subscribe((response) => {
          if (response.result) {
              const activeSellers = (response.result?.map((m) => SellerModel.deserializeAPIModel(m)) || []).filter(
                  (f) => f.isActive === true,
              );

              if (activeSellers.length === 0) return;

              purchaseLinkStore.updateSellers(activeSellers);
              purchaseLinkStore.updatePurchaseLink(
                  new PurchaseLinkModel({
                      ...purchaseLinkStore.purchaseLinkModel,
                      selectedSeller:
                          purchaseLinkStore.purchaseLinkModel.selectedSeller?.id?.length > 0
                              ? purchaseLinkStore.purchaseLinkModel.selectedSeller
                              : activeSellers[0],
                  }),
              );
          }
      });
  }

  const handleInputChange = (field: keyof PurchaseLinkModel, value: any) => {
      purchaseLinkStore.updatePurchaseLink(new PurchaseLinkModel({ ...purchaseLinkModel, [field]: value }));
  };

  const handleItemDetailInputChange = (field: keyof ItemDetailPageModel, value: string | boolean | number) => {
      purchaseLinkStore.updatePurchaseLink(
          new PurchaseLinkModel({
              ...purchaseLinkModel,
              itemDetails: new ItemDetailPageModel({ ...purchaseLinkModel.itemDetails, [field]: value }),
          }),
      );
  };

  const textInput = (
      field: keyof PurchaseLinkModel | keyof ItemDetailPageModel,
      type: 'text' | 'number',
      value: string | number,
      placeHolder: string,
      className: string,
      error: boolean,
      errorMessage: string,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void,
  ) => {
      return (
          <Input
              type={type}
              value={value}
              error={error}
              errorMessage={errorMessage}
              className={`styled-input merchant-inputs ${className}`}
              onChange={onChange}
              placeholder={placeHolder}
          />
      );
  };

  function checkBox(
      value: boolean,
      fieldName: keyof PurchaseLinkModel | keyof ItemDetailPageModel,
      label: string,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
  ) {
      return <Input value={value} id={`chk${fieldName}`} type="checkbox" label={label} onChange={onChange} />;
  }

  function singleSelect(
      selectedKey: string,
      fieldName: keyof PurchaseLinkModel,
      options: IOption[],
      hasError: boolean,
      onChange: (option: IOption) => void,
      disabled?: boolean,
  ) {
      return (
          <SingleSelect
              hasError={hasError}
              selectedKey={selectedKey}
              nameKeys={['label']}
              valueKeys={['value']}
              options={[options]}
              onChange={onChange}
              disabled={disabled}
              className="purchase-link-select"
          />
      );
  }

  const handleCheckboxChange = (variantId: string, isChecked: boolean) => {
      const updatedVariantIds = isChecked
          ? [...purchaseLinkModel.selectedVariantIds, variantId]
          : purchaseLinkModel.selectedVariantIds.filter((id) => id !== variantId);
      handleInputChange('selectedVariantIds', updatedVariantIds);
  };

  const handlePriceCheckboxChange = (priceId: string, isChecked: boolean) => {
      const updatedPriceIds = isChecked
          ? [...purchaseLinkModel.selectedPriceIds, priceId]
          : purchaseLinkModel.selectedPriceIds.filter((id) => id !== priceId);
      handleInputChange('selectedPriceIds', updatedPriceIds);
  };

  const renderVariantsOrPrice = () => {
      const variantOptions = getVariantOptions(purchaseLinkModel.isTrackInventory);
      if (purchaseLinkModel.itemType === SystemItemTypes.Product) {
          return (
              <div>
                  <CustomLabel>Variants</CustomLabel>
                  {singleSelect(
                      purchaseLinkModel.itemDetails?.hasVariants
                          ? purchaseLinkModel.variantsType
                          : variantOptions[1].value,
                      'variantsType',
                      variantOptions,
                      purchaseLinkModel.hasError(
                          nameof<PurchaseLinkModel>('variantsType' as keyof PurchaseLinkModel),
                      ),
                      (option) => handleInputChange('variantsType', option.value),
                      !purchaseLinkModel.itemDetails?.hasVariants,
                  )}
                  {purchaseLinkModel.variants &&
                      purchaseLinkModel.variantsType === PurchaseVariantSelectionType.SpecificVariants && (
                          <PurchaseLinkVariantTable
                              showOnHand={purchaseLinkModel.variants.showOnHand}
                              showPriceByVariant={purchaseLinkModel.variants.priceByVariant}
                              variants={purchaseLinkModel.variants.options}
                              selectedVariantIds={purchaseLinkModel.selectedVariantIds}
                              handleCheckboxChange={handleCheckboxChange}
                              priceDetails={purchaseLinkModel.pricingInputs && purchaseLinkModel.pricingInputs[0]}
                          />
                      )}
              </div>
          );
      } else if (purchaseLinkModel.itemType !== SystemItemTypes.Donation) {
          return (
              <div>
                  <CustomLabel>Price</CustomLabel>
                  {singleSelect(
                      purchaseLinkModel.priceType,
                      'priceType',
                      priceOptions,
                      purchaseLinkModel.hasError(nameof<PurchaseLinkModel>('priceType' as keyof PurchaseLinkModel)),
                      (option) => handleInputChange('priceType', option.value),
                      false,
                  )}
                  {purchaseLinkModel.priceType === PurchaseLinkPriceOption.Specific && (
                      <FlexContainer fxLayout="column" spacing={10} padding="15px 0px 0px">
                          {purchaseLinkModel.pricingInputs?.map((price) => {
                              const isChecked = purchaseLinkModel.selectedPriceIds.includes(price.id);
                              const isActive = price.isActive;
                              return (
                                  <div key={price.id}>
                                      <FlexContainer alignItems="center">
                                          <Input
                                              value={isChecked}
                                              id={`chk${price.id}`}
                                              type="checkbox"
                                              label=""
                                              onChange={(e) => handlePriceCheckboxChange(price.id, e.target.checked)}
                                              disabled={!isActive}
                                          />
                                          <CustomLabel className="pl-no-margin">{price.name}</CustomLabel>
                                      </FlexContainer>
                                  </div>
                              );
                          })}
                      </FlexContainer>
                  )}
              </div>
          );
      }
  };

  const renderLinkDetailsSection = () => {
      if (!purchaseLinkModel.itemDetails) return;
      return (
          <>
              <CustomLabel className="pl-header">Link Details</CustomLabel>
              <FlexContainer fxLayout="column" spacing={30} className="pl-input-labels">
                  <div>
                      <CustomLabel>Name</CustomLabel>
                      {textInput(
                          'name',
                          'text',
                          purchaseLinkModel.itemDetails?.name,
                          'Enter value',
                          '',
                          purchaseLinkModel.hasError(nameof<PurchaseLinkModel>('name' as keyof PurchaseLinkModel)),
                          '',
                          (e) => handleItemDetailInputChange('name', e.target.value),
                      )}
                  </div>
                  {purchaseLinkModel.itemType !== SystemItemTypes.Donation && renderVariantsOrPrice()}
                  {purchaseLinkModel.itemType !== SystemItemTypes.Donation && (
                      <div>
                          <CustomLabel>Promotion</CustomLabel>
                          {singleSelect(
                              purchaseLinkModel.promotionValue,
                              'promotionValue',
                              promotionOptions,
                              purchaseLinkModel.hasError(
                                  nameof<PurchaseLinkModel>('promotionValue' as keyof PurchaseLinkModel),
                              ),
                              (option) => handleInputChange('promotionValue', option.value),
                              false,
                          )}
                      </div>
                  )}
              </FlexContainer>
          </>
      );
  };

  const renderListOptions = () => {
      if (!purchaseLinkModel.itemDetails) return;
      return (
          <CollapsiblePanel title="List Options" className="pl-chkbx-panel">
              <FlexContainer alignItems="center" spacing={15} fxWrap="wrap" className="pl-chkbx-flex">
                  {checkBox(purchaseLinkModel.itemDetails?.tax, 'tax', 'Allow Tax', (e) =>
                      handleItemDetailInputChange('tax', e.target.checked),
                  )}
                  {checkBox(purchaseLinkModel.itemDetails?.surcharge, 'surcharge', 'Allow Surcharge', (e) =>
                      handleItemDetailInputChange('surcharge', e.target.checked),
                  )}
                  {checkBox(
                      purchaseLinkModel.includeRelatedItems,
                      'includeRelatedItems',
                      'Include Related Items',
                      (e) => handleInputChange('includeRelatedItems', e.target.checked),
                  )}
                  {purchaseLinkModel.itemType !== SystemItemTypes.Donation && (
                      <>
                          {checkBox(
                              purchaseLinkModel.chooseQuantity,
                              'chooseQuantity',
                              'Customer chooses quantity',
                              (e) => handleInputChange('chooseQuantity', e.target.checked),
                          )}
                          {checkBox(
                              purchaseLinkModel.showOriginalPrice || false,
                              'showOriginalPrice',
                              'Show Original Price',
                              (e) => handleInputChange('showOriginalPrice', e.target.checked),
                          )}
                          <FlexContainer spacing={10}>
                              {checkBox(
                                  purchaseLinkModel.itemDetails?.return || false,
                                  'return',
                                  'Allow Returns',
                                  (e) => handleItemDetailInputChange('return', e.target.checked),
                              )}
                              {purchaseLinkModel.itemDetails?.return && (
                                  <>
                                      <div className="return-days-input">
                                          {textInput(
                                              'returnDays',
                                              'number',
                                              purchaseLinkModel.itemDetails?.returnDays || 0,
                                              'Enter value',
                                              '',
                                              purchaseLinkModel.hasError(
                                                  nameof<PurchaseLinkModel>('returnDays' as keyof PurchaseLinkModel),
                                              ),
                                              '',
                                              (e) => handleItemDetailInputChange('returnDays', e.target.value),
                                          )}
                                      </div>
                                      <CustomLabel>days</CustomLabel>
                                  </>
                              )}
                          </FlexContainer>
                      </>
                  )}

                  {purchaseLinkModel.itemType === SystemItemTypes.Service ||
                      (purchaseLinkModel.itemType === SystemItemTypes.Membership && (
                          <>
                              <div>
                                  <FlexContainer spacing={10}>
                                      {checkBox(purchaseLinkModel.freeTrial, 'freeTrial', 'Free trial', (e) =>
                                          handleInputChange('freeTrial', e.target.checked),
                                      )}
                                      {purchaseLinkModel.freeTrial && (
                                          <>
                                              {' '}
                                              <div className="return-days-input">
                                                  {textInput(
                                                      'freeTrialDays',
                                                      'number',
                                                      purchaseLinkModel.freeTrialDays,
                                                      'Enter value',
                                                      '',
                                                      purchaseLinkModel.hasError(
                                                          nameof<PurchaseLinkModel>(
                                                              'freeTrialDays' as keyof PurchaseLinkModel,
                                                          ),
                                                      ),
                                                      '',
                                                      (e) => handleInputChange('freeTrialDays', e.target.value),
                                                  )}
                                              </div>
                                              <CustomLabel>days</CustomLabel>
                                          </>
                                      )}
                                  </FlexContainer>
                              </div>
                          </>
                      ))}
              </FlexContainer>
          </CollapsiblePanel>
      );
  };

  const checkRelatedItems = (relatedSectionData: RelatedSectionModel | null): boolean | null => {
      if (!relatedSectionData) return null;

      const { includedItems, recommendedItems, requiredItems } = relatedSectionData;

      const checkItemsBasedOnFlag = (relatedItems: RelatedItemsModel) => {
          if (relatedItems.showSelectItems) {
              return relatedItems.items.length > 0;
          } else if (relatedItems.showCategories) {
              return relatedItems.categories.length > 0;
          } else if (relatedItems.showAccountCredits) {
              return parseFloat(relatedItems.accountCredits) > 0;
          }
          return false;
      };

      return (
          (includedItems && checkItemsBasedOnFlag(includedItems)) ||
          (recommendedItems && checkItemsBasedOnFlag(recommendedItems)) ||
          (requiredItems && checkItemsBasedOnFlag(requiredItems))
      );
  };

  const navPills = () => {
      const hasRelatedItems = checkRelatedItems(purchaseLinkModel.relatedSectionData || null);
      if (hasRelatedItems) {
          return (
              <FlexContainer alignItems="center" padding="0px 0px 30px" className="pl-navpills">
                  <div className="flex-auto-grow">
                      <NavPills
                          defaultActiveKey="details"
                          pills={[
                              {
                                  key: 'details',
                                  title: 'DETAILS',
                              },
                              {
                                  key: 'related-items',
                                  title: 'RELATED ITEMS',
                              },
                          ]}
                          onSelect={(key) => setActiveNavPill(key)}
                      />
                  </div>
              </FlexContainer>
          );
      }
  };

  // const renderMedia = (media: MediaModel) => {
  //     switch (media.type) {
  //         case MediaTypes.Video:
  //             return <Video url={media.fullURL} />;
  //         default:
  //             return (
  //                 <img src={media.fullURL} style={{ height: '150px', width: '100px' }} className="product-image" />
  //             );
  //     }
  // };

  const renderPurchaseLinkContent = () => {
      return (
          <FlexContainer fxLayout="column">
              {navPills()}
              {activeNavPill === 'details' ? (
                  <>
                      <div className="width-inherit">
                          <FlexContainer>
                              <HalfContainer> {renderLinkDetailsSection()}</HalfContainer>
                              <HalfContainer>
                                  {' '}
                                  <CarouselComponent media={purchaseLinkModel.itemDetails?.medias || []} />
                                  {/* {purchaseLinkModel.itemDetails?.medias &&
                                      purchaseLinkModel.itemDetails?.medias?.length > 0 &&
                                      purchaseLinkModel.itemDetails?.medias.map((media, index) => (
                                          <FlexContainer key={index} size={0} justifyContent="center">
                                              {renderMedia(media)}
                                          </FlexContainer>
                                      ))} */}
                              </HalfContainer>
                          </FlexContainer>
                      </div>

                      {renderListOptions()}
                      <PurchaseLinkAdvancedSection
                          sellers={purchaseLinkStore.sellers}
                          handleInputChange={(field: keyof PurchaseLinkModel, value: MerchantAccount | IOption) =>
                              handleInputChange(field, value)
                          }
                          selectedMerchantAccount={purchaseLinkModel.selectedMerchantAccount}
                          selectedSeller={purchaseLinkModel.selectedSeller}
                          merchantAccounts={purchaseLinkStore.merchantAccounts}
                          merchantHasError={purchaseLinkModel.hasError(
                              nameof<PurchaseLinkModel>('selectedMerchantAccount' as keyof PurchaseLinkModel),
                          )}
                          sellerHasError={purchaseLinkModel.hasError(
                              nameof<PurchaseLinkModel>('selectedSeller' as keyof PurchaseLinkModel),
                          )}
                      />
                  </>
              ) : (
                  <PurchaseLinkRelatedItems relatedSectionData={purchaseLinkModel.relatedSectionData} />
              )}
          </FlexContainer>
      );
  };

  const onClickSave = async () => {
      try {
          const isValidate = purchaseLinkModel.validate();
          if (!isValidate) {
              purchaseLinkStore.updatePurchaseLink(new PurchaseLinkModel({ ...purchaseLinkModel }));
              return;
          }

          const apiModel = purchaseLinkModel.createAPIModel();
          let response;

          if (isEdit) {
              response = await PurchaseLinkService.updatePurchaseLink(apiModel).toPromise();
          } else {
              if (purchaseLinkModel.isNew) {
                  response = await PurchaseLinkService.createPurchaseLink(apiModel).toPromise();
              } else {
                  response = await PurchaseLinkService.updatePurchaseLink(apiModel).toPromise();
              }
          }

          if (response?.result) {
              onClose(true);
              if (isEdit) {
                  ToastService.showSuccess('Purchase link updated successfully!');
              } else {
                  if (purchaseLinkModel.isNew) {
                      ToastService.showSuccess('Purchase link created successfully!');
                  } else {
                      ToastService.showSuccess('Purchase link updated successfully!');
                  }
              }
          } else {
              ToastService.showError('Error in creating purchase link');
          }
      } catch (error) {
          ToastService.showError('Error Occurred');
      }
  };

  return (
      <PurchaseLinkPopupWrapper>
          {purchaseLinkModel.isOpen && (
              <ModalComponent
                  title={`${purchaseLinkModel.itemDetails?.name} - Purchase Link`}
                  titleFontSize="22px"
                  bodyPadding="40px"
                  showFooter={true}
                  minHeight="300px"
                  wrapperWidth="1000px"
                  bodyBackgroundColor="#fefefe"
                  footerBackground="#F4F7FB"
                  footerHeight="auto"
                  onClose={() => onClose(false)}
                  footerContent={
                      <FlexContainer alignItems="center" justifyContent="center" spacing={15}>
                          <SapphireBlueButton minWidth="120px" height="40px" onClick={() => onClose(false)}>
                              Close
                          </SapphireBlueButton>
                          <SapphireBlueButton primary minWidth="120px" height="40px" onClick={() => onClickSave()}>
                              Save
                          </SapphireBlueButton>
                      </FlexContainer>
                  }
              >
                  {renderPurchaseLinkContent()}
              </ModalComponent>
          )}
      </PurchaseLinkPopupWrapper>
  );
};
export default observer(PurchaseLinkPopUp);
